import * as React from 'react'
import { cn } from '../../lib/utils'
import {
  RichTextEditor,
  type RichTextCommand,
} from '../core/rich-text-editor'
import { SegmentedControl } from '../core/segmented-control'
import {
  Select,
  SelectTrigger,
  SelectContent,
  SelectItem,
} from '../core/select'
import { MultiCombobox } from '../core/multi-combobox'
import { Field, FieldLabel } from '../core/field'
import { Input } from '../core/input'
import {
  activityTypeConfig,
  type ActivityType,
} from '../core/activity-icons'

/**
 * ActivityComposer — the CRM "log an activity" surface for a deal / company
 * header. A type switcher (Note · Call · Meeting · Trade show) picks what you're
 * logging; each type reveals its own light form above a shared rich-text notes
 * area, and one footer logs the whole entry. The logged item is the consumer's
 * to persist and render (e.g. into an `ActivityTimeline`) — this block owns the
 * *composition chrome*, not the data.
 *
 *   <ActivityComposer onLog={(type) => save(type)} onCommand={editor.toggle} />
 *
 * Pass `types={['note']}` to collapse it to a plain notes composer (the company
 * header) — the switcher hides when there's a single type, so it renders as just
 * the editor. Like `RichTextEditor`, it bundles no editing engine: wire the
 * toolbar via `onCommand` and mount your engine on `contentRef`. The per-type
 * fields (call outcome/duration, meeting date/attendees, …) are presentational
 * chrome for this first cut — the consumer reads their values on submit.
 */

/** The activity kinds this composer can log (a subset of `ActivityType`). */
export type ActivityComposerType = Extract<
  ActivityType,
  'note' | 'call' | 'meeting' | 'tradeShow'
>

const DEFAULT_TYPES: ActivityComposerType[] = [
  'note',
  'call',
  'meeting',
  'tradeShow',
]

/** Per-type footer verb + notes placeholder. */
const TYPE_META: Record<
  ActivityComposerType,
  { logLabel: string; placeholder: string }
> = {
  note: { logLabel: 'Save note', placeholder: 'Start writing your note here' },
  call: { logLabel: 'Log call', placeholder: 'Add call notes…' },
  meeting: { logLabel: 'Log meeting', placeholder: 'Add meeting notes…' },
  tradeShow: { logLabel: 'Log trade show', placeholder: 'Add trade show notes…' },
}

export interface ActivityComposerProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, 'onChange'> {
  /** Which activity kinds to offer (order preserved). Default: all four. */
  types?: ActivityComposerType[]
  /** Selected type (controlled). */
  activeType?: ActivityComposerType
  /** Initial type (uncontrolled). */
  defaultType?: ActivityComposerType
  /** Fired when the type switcher changes. */
  onTypeChange?: (type: ActivityComposerType) => void
  /** Fired by the footer Log action with the active type. Renders the footer. */
  onLog?: (type: ActivityComposerType) => void
  /** Fired by the footer Discard action. */
  onDiscard?: () => void
  /** Forwarded to the notes editor toolbar buttons. */
  onCommand?: (command: RichTextCommand) => void
  /** Toolbar commands to render pressed. */
  activeCommands?: RichTextCommand[]
  /** Ref to the notes editable surface (mount an engine here). */
  contentRef?: React.Ref<HTMLDivElement>
  /** Override the notes placeholder (defaults per type). */
  notePlaceholder?: string
  /** Disable the whole composer. */
  disabled?: boolean
}

/** The two-up field row for a given activity type (null for a plain note). */
const TypeFields = ({
  type,
  disabled,
}: {
  type: ActivityComposerType
  disabled?: boolean
}) => {
  // Unique base so each FieldLabel `htmlFor` ties to its input `id` even with
  // multiple composers on a page (FieldLabel doesn't auto-associate).
  const uid = React.useId()
  if (type === 'note') return null

  const rows: Record<
    Exclude<ActivityComposerType, 'note'>,
    [React.ReactNode, React.ReactNode]
  > = {
    call: [
      <Field key="outcome" disabled={disabled}>
        <FieldLabel>Outcome</FieldLabel>
        <Select
          disabled={disabled}
          aria-label="Call outcome"
          placeholder="Select outcome"
          defaultSelectedKey="connected"
          className="w-full"
        >
          <SelectTrigger />
          <SelectContent>
            <SelectItem id="connected">Connected</SelectItem>
            <SelectItem id="voicemail">Left voicemail</SelectItem>
            <SelectItem id="no-answer">No answer</SelectItem>
          </SelectContent>
        </Select>
      </Field>,
      <Field key="duration" disabled={disabled}>
        <FieldLabel htmlFor={`${uid}-duration`}>Duration</FieldLabel>
        <Input id={`${uid}-duration`} placeholder="e.g. 15 min" disabled={disabled} />
      </Field>,
    ],
    meeting: [
      <Field key="when" disabled={disabled}>
        <FieldLabel htmlFor={`${uid}-when`}>Date &amp; time</FieldLabel>
        <Input id={`${uid}-when`} type="datetime-local" disabled={disabled} />
      </Field>,
      <Field key="attendees" disabled={disabled}>
        <FieldLabel>Attendees</FieldLabel>
        {/* Multi-select that also accepts people not in the system: type a name
            and "Add …" commits it as a free-text attendee. */}
        <MultiCombobox
          aria-label="Attendees"
          allowsCustomValue
          placeholder="Add people…"
          disabled={disabled}
          options={[
            { value: 'sarah-chen', label: 'Sarah Chen' },
            { value: 'marco-diaz', label: 'Marco Diaz' },
            { value: 'priya-patel', label: 'Priya Patel' },
          ]}
        />
      </Field>,
    ],
    tradeShow: [
      <Field key="event" disabled={disabled}>
        <FieldLabel htmlFor={`${uid}-event`}>Event</FieldLabel>
        <Input id={`${uid}-event`} placeholder="e.g. Natural Products Expo" disabled={disabled} />
      </Field>,
      <Field key="booth" disabled={disabled}>
        <FieldLabel htmlFor={`${uid}-booth`}>Booth / location</FieldLabel>
        <Input id={`${uid}-booth`} placeholder="e.g. Hall B, #2145" disabled={disabled} />
      </Field>,
    ],
  }

  const [left, right] = rows[type]
  return (
    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
      {left}
      {right}
    </div>
  )
}

export const ActivityComposer = React.forwardRef<
  HTMLDivElement,
  ActivityComposerProps
>(
  (
    {
      types = DEFAULT_TYPES,
      activeType,
      defaultType,
      onTypeChange,
      onLog,
      onDiscard,
      onCommand,
      activeCommands,
      contentRef,
      notePlaceholder,
      disabled = false,
      className,
      ...props
    },
    ref
  ) => {
    const isControlled = activeType !== undefined
    const [internal, setInternal] = React.useState<ActivityComposerType>(
      defaultType ?? types[0]
    )
    const active = isControlled ? activeType : internal
    // Guard against a `types` list that doesn't include the active value.
    const current = types.includes(active) ? active : types[0]

    const selectType = (value: string) => {
      const next = value as ActivityComposerType
      if (!isControlled) setInternal(next)
      onTypeChange?.(next)
    }

    const meta = TYPE_META[current]
    const showSwitcher = types.length > 1

    return (
      <div ref={ref} className={cn('flex flex-col gap-3', className)} {...props}>
        {showSwitcher && (
          <SegmentedControl
            variant="subtle"
            disabled={disabled}
            aria-label="Activity type"
            value={current}
            onValueChange={selectType}
            options={types.map((t) => ({
              value: t,
              label: activityTypeConfig[t].label,
              icon: activityTypeConfig[t].icon,
            }))}
          />
        )}

        <TypeFields type={current} disabled={disabled} />

        <RichTextEditor
          disabled={disabled}
          placeholder={notePlaceholder ?? meta.placeholder}
          contentRef={contentRef}
          contentLabel={`${activityTypeConfig[current].label} notes`}
          onCommand={onCommand}
          activeCommands={activeCommands}
          // The footer appears when `onLog` or `onDiscard` is wired; omit both
          // for a bare, auto-saving field (the note-only company composer).
          onSend={onLog ? () => onLog(current) : undefined}
          onDiscard={onDiscard}
          sendLabel={meta.logLabel}
        />
      </div>
    )
  }
)
ActivityComposer.displayName = 'ActivityComposer'
